import { SearchOutlined } from "@ant-design/icons";
import { Col, Input, Row, Typography, Form, Select } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AppDispatch,
  dynamicSet,
  getSubjectList,
  getSubjectListClear,
  getSubjectListRequest,
  useDynamicSelector,
} from "@src/redux";
import { filterOptions } from "@src/helpers/constants";
import DynamicTreeSelect from "../../common/TreeSelect";
import { startCase } from "lodash";
import Scrollbars from "react-custom-scrollbars-2";

const { Title, Text } = Typography;

const SubjectFilterForm: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { result: userResult } = useSelector(getSubjectList);
  const [form] = Form.useForm();

  const status = Form.useWatch("status", form);
  const myOrg = Form.useWatch("my_org", form);
  const { filter: savedFilter } = useDynamicSelector("subject_filter");
  const { pagination: subjectPagination } =
    useDynamicSelector("subject_pagination");

  const [showTitle, setShowTitle] = useState<boolean>(false);
  const handleScroll = (value: { scrollTop: number }) => {
    setShowTitle(value.scrollTop > 10);
  };

  const [searchValue, setSearchValue] = useState(
    savedFilter?.searchString || ""
  );
  const hasInitialized = useRef(false);
  const initialPagination = { pageLimit: 10, pageNumber: 1 };

  useEffect(() => {
    form.setFieldsValue({
      search: savedFilter?.searchString || "",
      status: savedFilter?.status
        ? savedFilter.status.toLowerCase()
        : "all",
      myOrg: savedFilter?.organizationId,
    });
    setSearchValue(savedFilter?.searchString || "");
  }, [form, savedFilter]);

  const buildFilters = (searchInput: string) => {
    const normalizedStatus = status ? startCase(status) : undefined;
    return {
      searchString: searchInput,
      status: normalizedStatus,
      organizationId: myOrg,
    };
  };

  const requestList = (
    filters: Record<string, any>,
    pagination: { pageLimit: number; pageNumber: number }
  ) => {
    dispatch(dynamicSet("subject_filter", { filter: filters }));
    dispatch(dynamicSet("subject_pagination", { pagination }));
    dispatch(
      getSubjectListRequest({
        filter: filters,
        pagination,
        sort: [{ field: "name", order: "asc" }],
      })
    );
  };

  const triggerSearch = (searchInput: string) => {
    if (!myOrg) return;
    requestList(buildFilters(searchInput), initialPagination);
  };

  useEffect(() => {
    if (hasInitialized.current || !myOrg) return;
    hasInitialized.current = true;
    const pagination = subjectPagination || initialPagination;
    requestList(buildFilters(savedFilter?.searchString || ""), pagination);
  }, [subjectPagination, myOrg, savedFilter]);

  useEffect(() => {
    if (!hasInitialized.current || !myOrg) return;
    requestList(buildFilters(searchValue), initialPagination);
  }, [myOrg, status]);

  useEffect(() => {
    dispatch(getSubjectListClear());
  }, [dispatch]);

  return (
    <div className="filter-container">
      <div>
        <Row className="header-filter" align="middle">
          <Col span={24}>
            <Title level={4} className="client-filters-header-title">
              Filters
            </Title>
          </Col>
        </Row>
        <Scrollbars
          id="sidebar"
          className="filter_sidebar_content"
          onScrollFrame={handleScroll}
        >
          <Form layout="vertical" className="common-filters" form={form}>
            <Row className="filter-row" gutter={[16, 16]}>
              <Col span={24}>
                <Form.Item
                  label="Search"
                  className="filter-label"
                  name="search"
                >
                  <Input
                    prefix={<SearchOutlined />}
                    className="filter-input"
                    allowClear
                    value={searchValue}
                    onChange={(e) => {
                      setSearchValue(e.target.value);
                      if (e.target.value === "") {
                        form.setFieldsValue({ search: "" });
                        triggerSearch("");
                      }
                    }}
                    onPressEnter={(e) => {
                      form.setFieldsValue({ search: searchValue });
                      triggerSearch(searchValue);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="status"
                  label="Status"
                  initialValue={"all"}
                >
                  <Select
                    options={filterOptions.subjectStatus}
                    allowClear
                    defaultValue={"all"}
                    placeholder="All"
                    className="custom-placeholder"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <DynamicTreeSelect
                form={form}
                col={24}
                mutationType="add"
                isRequired={false}
                allowClear={true}
                className="test"
              />
            </Row>
          </Form>
        </Scrollbars>
      </div>
      <Row className="footer">
        <Col>
          <Text strong style={{ fontSize: "20px" }}>
            Total
          </Text>
        </Col>
        <Col className="total-value">
          <Text strong style={{ color: "#ffffff", fontSize: "17px" }}>
            {userResult?.pagination?.totalCount || 0}
          </Text>
        </Col>
      </Row>
    </div>
  );
};

export default SubjectFilterForm;
